import { Table, TableColumnsType } from "antd";
import { FC, useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { deleteExamPaper, getExamSubjects, updateExamPaper } from "../../../api/testCenter.api";
import useAxios from "../../../hooks/useAxios";
import { IGetExamSubjects } from "../../../models/testCenter.model";
import ErrorResponse from "../../ErrorResponse";
import Button from '../../Button';
import SelectPaperModal from "../../modals/SelectPaperModal";
import useNotification from "../../../hooks/useNotification";
import Loader from "../../Loader";
import useAuth from "../../../hooks/useAuth";

interface IProps {
  examCode: number|undefined;
  isPublished: boolean;
}

const TestSubject: FC<IProps> = ({ examCode, isPublished }) => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const { successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetExamSubjects>();
  const { data: updateRes, isLoading: updateIsLoading, error: updateError, execute: updateExecute } = useAxios();
  const { data: deleteRes, isLoading: deleteIsLoading, error: deleteError, execute: deleteExecute } = useAxios();

  const [examSubjectCode, setExamSubjectCode] = useState<number | undefined>();
  const [selectPaperVisible, setSelectPaperVisible] = useState(false);
  const [subjectTimes, setSubjectTimes] = useState<string[]>([]);

  useEffect(() => {
    if (examCode) {
      doGetExamSubjects();
    }
  }, [examCode]);

  useEffect(() => {
    if (res && !isLoading) {
      setSubjectTimes(res.data.map(item => String(item.subjectTime / 60)));
    }
  }, [res, isLoading]);

  useEffect(() => {
    if (deleteRes && !deleteIsLoading) {
      doGetExamSubjects();
      successNotification(t('common.message.delete'));
    }
  }, [deleteRes, deleteIsLoading]);

  useEffect(() => {
    if (updateRes && !updateIsLoading) {
      successNotification(t('common.message.save1'));
    }
  }, [updateRes, updateIsLoading]);

  const doGetExamSubjects = () => {
    if (!examCode) return;
    execute(getExamSubjects(examCode));
  }

  const onSetSubjectTimes = (index: number, value: string) => {
    const _subjectTimes = [...subjectTimes];
    _subjectTimes[index] = value;
    setSubjectTimes(_subjectTimes);
  }

  const columns: TableColumnsType<any> = [
    {
      title: t('cbt.type.column5'),
      dataIndex: 'subjectName',
      key: 'subjectName',
      align: 'center',
    },
    {
      title: t('cbt.test.column1'),
      dataIndex: 'testTitle',
      key: 'testTitle',
      align: 'center',
      render: (value: string, record: any) => value ? value : `(${t('cbt.test.column13')})`
    },
    {
      title: t('cbt.test.column14'),
      dataIndex: 'examSubjectCode',
      key: 'examSubjectCode',
      align: 'center',
      render: (value: number, record: any) => {
        if (record.testCode) {
          return (
            <div className="comm-btn__area justify-content-center">
              <Button
                className='btn-basic small gray'
                onClick={() => {
                  deleteExecute(deleteExamPaper(value));
                }}
                text={t('common.button.delete')}
                writeAuthYn={writeAuthYn}
                disabled={isPublished}
              />
            </div>
          )
        } else {
          return (
            <div className="comm-btn__area justify-content-center">
              <Button
                className='btn-basic small pink'
                onClick={() => {
                  setExamSubjectCode(value);
                  setSelectPaperVisible(true);
                }}
                text={t('cbt.test.button1')}
                writeAuthYn={writeAuthYn}
                disabled={isPublished}
              />
            </div>
          )
        }
      }
    },
    {
      title: t('cbt.test.column15'),
      dataIndex: 'quizCount',
      key: 'quizCount',
      align: 'center',
      render: (value: string, record: any) => record.testCode ? value : "N/A"
    },
    {
      title: t('cbt.test.column16'),
      dataIndex: 'subjectTime',
      key: 'subjectTime',
      align: 'center',
      render: (value: string, record: any, index: number) => record.testCode
        ? (
          <div className="flex flex-justify-center">
            <input
              type="number"
              className="form-control w-60p"
              value={subjectTimes[index]}
              onChange={e => onSetSubjectTimes(index, e.target.value)}
            />
          </div>
          )
        : "N/A"
    },
    {
      title: t('common.button.save'),
      dataIndex: 'examSubjectCode',
      key: 'examSubjectCode',
      align: 'center',
      render: (value: number, record: any, index: number) => (
        <div className="comm-btn__area justify-content-center">
          <Button
            className='btn-basic small pink'
            onClick={() => {
              updateExecute(updateExamPaper(value, { subjectTime: Number(subjectTimes[index]) * 60 }));
            }}
            text={t('common.button.save')}
            disabled={!record.testCode || isPublished}
            writeAuthYn={writeAuthYn}
          />
        </div>
      )
    },
  ];

  return (
    <>
      {isLoading || updateIsLoading || deleteIsLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={updateError} />
      <ErrorResponse error={deleteError} />

      <div className='comm-table__area'>
        <Table
          columns={columns}
          dataSource={res?.data}
          pagination={{
            pageSize: 99999
          }}
          rowKey="examSubjectCode"
        />
      </div>

      {selectPaperVisible && (
        <SelectPaperModal
          visible={selectPaperVisible}
          setVisible={setSelectPaperVisible}
          examSubjectCode={examSubjectCode}
          doGetExamSubjects={doGetExamSubjects}
        />
      )}
    </>
  );
}

export default TestSubject;
